import { state, updateState, getAuthState, getUserRole } from './state/store.js';
import {
  initDocumentation,
  loadCollections,
  loadDocumentsInCollection,
  loadDocumentation,
} from './components/documentation.js';
import { initChat, refreshWelcomeMessage, applyKbOptions } from './components/chat.js';
import { getBranding as fetchBranding } from './api/client.js';
import { setBranding } from './state/branding.js';
import { initAdmin, loadKnowledgeBases } from './components/admin.js';
import { initAnalytics, loadAnalytics } from './components/analytics.js';
import { initCallcenterAnalytics, loadCallcenterAnalytics } from './components/callcenter-analytics.js';
import { initAuth, setupTokenExpirationCheck } from './components/auth.js';
import { initBrandingAdmin, loadBrandingAdmin } from './components/branding-admin.js';
import './styles/main.css';

// "Wissensdatenbank" / "Dokumente" — admin only (document/knowledge-base management).
const ADMIN_ONLY_TABS = new Set(['documentation', 'admin', 'branding']);
// "Analytik" / "Call Center" — admin and callcenter (both roles review interactions).
const STAFF_TABS = new Set(['analytics', 'ccAnalytics', 'hilfe']);

function applyRoleBasedTabAccess() {
  const role = getUserRole();
  // kommunen accounts (and anyone unauthenticated/unrecognized) only ever see Chat.
  const allowedTabs = new Set(['chat']);
  if (role === 'admin') {
    ADMIN_ONLY_TABS.forEach((tab) => allowedTabs.add(tab));
    STAFF_TABS.forEach((tab) => allowedTabs.add(tab));
  } else if (role === 'callcenter') {
    STAFF_TABS.forEach((tab) => allowedTabs.add(tab));
  }

  document.querySelectorAll('.nav-tab[data-tab]').forEach((tab) => {
    if (ADMIN_ONLY_TABS.has(tab.dataset.tab) || STAFF_TABS.has(tab.dataset.tab)) {
      tab.style.display = allowedTabs.has(tab.dataset.tab) ? '' : 'none';
    }
  });

  if (!allowedTabs.has(state.currentTab)) {
    switchTab('chat');
  }
}

// Tab switching
function switchTab(tabName) {
  updateState({ currentTab: tabName });

  const navTabs = document.querySelectorAll('.nav-tab');
  const chatTab = document.getElementById('chatTab');
  const documentationTab = document.getElementById('documentationTab');
  const adminTab = document.getElementById('adminTab');
  const brandingTab = document.getElementById('brandingTab');
  const hilfeTab = document.getElementById('hilfeTab');
  const analyticsTab = document.getElementById('analyticsTab');
  const ccAnalyticsTab = document.getElementById('ccAnalyticsTab');

  // Update tab buttons
  navTabs.forEach((tab) => {
    if (tab.dataset.tab === tabName) {
      tab.classList.add('active');
    } else {
      tab.classList.remove('active');
    }
  });

  // Hide all tabs
  chatTab.classList.remove('active');
  documentationTab.classList.remove('active');
  adminTab.classList.remove('active');
  if (brandingTab) brandingTab.classList.remove('active');
  if (hilfeTab) hilfeTab.classList.remove('active');
  analyticsTab.classList.remove('active');
  if (ccAnalyticsTab) ccAnalyticsTab.classList.remove('active');

  // Show selected tab
  if (tabName === 'documentation') {
    documentationTab.classList.add('active');

    if (state.availableCollections.length === 0) {
      loadCollections();
    }
    if (state.availableDocuments.length === 0) {
      loadDocumentsInCollection();
    }
    if (state.allDocChunks.length === 0) {
      loadDocumentation();
    }
  } else if (tabName === 'chat') {
    chatTab.classList.add('active');
  } else if (tabName === 'admin') {
    adminTab.classList.add('active');
    // Loads the KB list, fills the dropdowns, and refreshes the document list
    loadKnowledgeBases();
  } else if (tabName === 'branding') {
    brandingTab.classList.add('active');
    loadBrandingAdmin();
  } else if (tabName === 'hilfe') {
    hilfeTab.classList.add('active');
  } else if (tabName === 'analytics') {
    analyticsTab.classList.add('active');
    loadAnalytics();
  } else if (tabName === 'ccAnalytics') {
    ccAnalyticsTab.classList.add('active');
    loadCallcenterAnalytics();
  }
}

// Fetch branding and apply it to every branded surface. Falls back silently
// to the static markup when the backend is unreachable.
// Monotonic guard: applyBranding runs on load and again after login. Without
// it, a slow anonymous response landing after the authenticated one overwrote
// the group's KB list and branding with the anonymous defaults.
let brandingRequestId = 0;

async function applyBranding() {
  const requestId = ++brandingRequestId;
  let branding;
  try {
    branding = await fetchBranding();
  } catch {
    return;
  }
  if (requestId !== brandingRequestId) return; // superseded by a newer fetch
  setBranding(branding);
  applyKbOptions(branding.selectable_kbs);

  document.title = branding.app_name;
  const logo = document.querySelector('h1.logo');
  if (logo) logo.textContent = branding.app_name;
  const tagline = document.querySelector('.tagline');
  if (tagline) tagline.textContent = branding.tagline;
  const loginTitle = document.querySelector('#loginModal h2');
  if (loginTitle) loginTitle.textContent = branding.app_name;

  // The chat owns the footer privacy link (it follows the KB selection,
  // updated via applyKbOptions above).
  refreshWelcomeMessage();
}

// Initialize application
document.addEventListener('DOMContentLoaded', () => {
  // Initialize auth first
  initAuth();
  setupTokenExpirationCheck();

  // Initialize components
  initDocumentation();
  initChat();
  initAdmin();
  initBrandingAdmin();
  initAnalytics();
  initCallcenterAnalytics();

  // Saved branding may affect the current page — re-apply immediately
  window.addEventListener('branding-changed', () => applyBranding());

  // Branding: once for the anonymous default, again after login for group overrides
  applyBranding();

  // Load data when authenticated
  const authState = getAuthState();
  applyRoleBasedTabAccess();
  if (authState.isAuthenticated && getUserRole() === 'admin') {
    loadKnowledgeBases();
    loadCollections().then(() => {
      loadDocumentsInCollection();
      loadDocumentation();
    });
  }

  // Listen for successful authentication
  window.addEventListener('auth-success', () => {
    applyBranding();
    applyRoleBasedTabAccess();
    if (getUserRole() === 'admin') {
      loadKnowledgeBases();
      loadCollections().then(() => {
        loadDocumentsInCollection();
        loadDocumentation();
      });
    }
  });

  // Setup tab navigation
  const navTabs = document.querySelectorAll('.nav-tab');
  navTabs.forEach((tab) => {
    tab.addEventListener('click', () => switchTab(tab.dataset.tab));
  });

  // Context help buttons: jump to the matching topic in the Hilfe tab
  document.querySelectorAll('.help-btn[data-help]').forEach((btn) => {
    btn.addEventListener('click', () => openHelpTopic(btn.dataset.help));
  });
});

function openHelpTopic(topicId) {
  switchTab('hilfe');
  const topic = document.getElementById(topicId);
  if (!topic) return;
  // Open only the requested topic and draw the eye to it
  document.querySelectorAll('.help-topic[open]').forEach((other) => {
    if (other !== topic) other.removeAttribute('open');
  });
  topic.setAttribute('open', '');
  topic.classList.remove('help-topic--flash');
  requestAnimationFrame(() => {
    topic.scrollIntoView({ behavior: 'smooth', block: 'start' });
    topic.classList.add('help-topic--flash');
  });
}
